/* eslint-disable @typescript-eslint/no-empty-function */
/**
 * [2023-08-01.1240 by Brian]
 *    Added support for toast names and removing toasts by that name.
 *    This provides the ability to close one or more toasts at the same
 *    time. The id of the toast is not always easy to capture and use
 *    even though it is returned by createToast.
 *
 *    I also disabled the ESLINT error about empty functions.
 *
 * [Sonner migration]
 *    The provider's public API is preserved so the 200+ call sites do
 *    not need to change. Internally, createToast delegates to sonner's
 *    toast() function. Sonner manages auto-dismiss, hover-pause, and
 *    rendering — so resetToastDuration / removeToastTimeout are now
 *    no-ops, and the local `toasts` array is no longer kept in state
 *    (nothing outside this file ever read it).
 *
 *    `isToast` is still observed by `Modal.tsx` to suppress
 *    click-outside-closes-modal while a toast is visible, so we track
 *    an active count via sonner's onDismiss / onAutoClose callbacks.
 */
import { AlertCircleIcon } from 'lucide-react';
import React, { useCallback, useRef, useState } from 'react';
import { toast as sonnerToast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';

export enum ToastTypes {
  Success,
  Fail,
  Info,
}

type IToast = {
  title: string;
  description: React.ReactNode;
  type: ToastTypes;
  duration?: number;
  id?: string;
  open?: boolean;
  name?: string;
  stack?: any;
  onClose?: () => void;
};

export const ToastContext = React.createContext<{
  toasts: IToast[];
  createToast: (data: IToast) => string;
  removeToast: (id: string) => void;
  removeToastByName: (name: string) => void;
  resetToastDuration: (id: string, duration: number) => void;
  removeToastTimeout: (id: string) => void;
  name?: string;
  isToast: boolean;
}>({
  toasts: [],
  createToast: (): string => {
    return '';
  },
  removeToast: () => {},
  resetToastDuration: () => {},
  removeToastTimeout: () => {},
  removeToastByName: () => {},
  isToast: false,
});

type Props = {
  children?: React.ReactNode;
};

const EMPTY_TOASTS: IToast[] = [];

export const ToastProvider: React.FC<Props> = ({ children }) => {
  const [activeCount, setActiveCount] = useState(0);
  const nameToIdsRef = useRef<Map<string, Set<string>>>(new Map());

  const releaseName = useCallback((id: string, name?: string) => {
    if (!name) return;
    const set = nameToIdsRef.current.get(name);
    if (!set) return;
    set.delete(id);
    if (set.size === 0) nameToIdsRef.current.delete(name);
  }, []);

  const removeToast = useCallback((id: string) => {
    if (!id) return;
    sonnerToast.dismiss(id);
  }, []);

  const removeToastByName = useCallback((name: string) => {
    if (!name) return;
    const ids = nameToIdsRef.current.get(name);
    if (!ids) return;
    // Copy because dismiss → onDismiss → releaseName mutates the set.
    Array.from(ids).forEach(id => sonnerToast.dismiss(id));
  }, []);

  const createToast = (toast: IToast): string => {
    let description = toast.description;
    if (description && typeof description !== 'string') {
      const d = description as any;
      if (typeof d?.response?.data?.message === 'string') {
        description = d.response.data.message;
      } else if (typeof d?.message === 'string') {
        description = d.message;
      }
    }

    const id = uuidv4();

    if (toast.name) {
      const existing = nameToIdsRef.current.get(toast.name) ?? new Set<string>();
      existing.add(id);
      nameToIdsRef.current.set(toast.name, existing);
    }

    setActiveCount(c => c + 1);
    const finish = () => {
      setActiveCount(c => Math.max(0, c - 1));
      releaseName(id, toast.name);
      toast.onClose?.();
    };

    // Original semantics: toasts without an explicit duration stayed open
    // until manually dismissed. Sonner's equivalent is Infinity.
    const baseOptions = {
      id,
      description,
      duration: toast.duration ?? Infinity,
      onDismiss: finish,
      onAutoClose: finish,
    };

    if (toast.type === ToastTypes.Success) {
      sonnerToast.success(toast.title, baseOptions);
    } else if (toast.type === ToastTypes.Info) {
      sonnerToast.info(toast.title, baseOptions);
    } else if (toast.type === ToastTypes.Fail) {
      // Preserve click-icon-to-copy-stack debug affordance.
      const icon =
        toast.stack !== undefined ? (
          <button
            type="button"
            aria-label="Copy error details to clipboard"
            onClick={() => {
              try {
                navigator.clipboard?.writeText(JSON.stringify(toast.stack));
              } catch {
                // ignore — best-effort dev helper
              }
            }}
            className="inline-flex"
          >
            <AlertCircleIcon className="h-5 w-5 text-red-600" />
          </button>
        ) : undefined;
      sonnerToast.error(toast.title, { ...baseOptions, icon });
    }

    return id;
  };

  // No-ops: sonner handles auto-dismiss + hover-pause natively, so the
  // pause/resume helpers Toast.tsx used to call are no longer needed.
  // Keep them on the context to satisfy the public type.
  const resetToastDuration = useCallback((_id: string, _duration: number) => {}, []);
  const removeToastTimeout = useCallback((_id: string) => {}, []);

  return (
    <ToastContext.Provider
      value={{
        toasts: EMPTY_TOASTS,
        createToast,
        removeToast,
        removeToastByName,
        resetToastDuration,
        removeToastTimeout,
        isToast: activeCount > 0,
      }}
    >
      {children}
    </ToastContext.Provider>
  );
};
